import { GanttTask, Dependency, GanttUser, Partner } from '../types/gantt.types'
import { SiteInfo } from '../views/CalendarMultiView'
import { useInvalidateSignals } from './useTaskSignals'
import { useState, useRef, useCallback, useEffect } from 'react'
import { useImmediateConfirm, isFixedPatchResponse, IMMEDIATE_CONFIRM_SUCCESS_MESSAGE } from './useImmediateConfirm'
import { getPartnerSaveKind } from '../constants/partnerStatus'
import { FOREMAN_CHANGE_MESSAGE } from '../constants/foremanSendMessage'
import { apiClient, isDirectRecord } from '@indipia/shared'
import { toast } from 'sonner'
import { releasePartner } from '../api/foremanApi'
import { buildPredecessorPatch, INVALID_PREDECESSOR_MESSAGE, isInvalidPredecessorError, samePredecessorSet } from '../views/scheduleUtils'
import { buildTaskDeleteConfirm, sumCounts } from '@indipia/shared-features'
import { confirmTaskDelete } from './confirmTaskDelete'
// 상세창 폼·편집중(dirty) 계산·이탈 확인·저장·삭제·이름 blur·선택 삭제 — 이동 전 GanttView.tsx 본문을 그대로 옮긴 것이다.

export type UseGanttPanelArgs = {
  tasks: GanttTask[]
  deps: Dependency[]
  onTaskPanelChange: ((open: boolean) => void) | undefined
  tasksRef: React.MutableRefObject<GanttTask[]>
  depsRef: React.MutableRefObject<Dependency[]>
  selectedSite: SiteInfo | undefined
  user: GanttUser
  setTasks: React.Dispatch<React.SetStateAction<GanttTask[]>>
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>
  invalidateSignals: ReturnType<typeof useInvalidateSignals>
  selectedTaskIds: Set<number>
  setSelectedTaskIds: React.Dispatch<React.SetStateAction<Set<number>>>
  /** 스케줄 전용 행정 공정(공종 마스터 scheduleOnlyYn='Y') 공종코드 — 시공·자재가 없어 상세창을 열지 않는다 */
  scheduleOnlyCodes: Set<string>
  /** 시공파트너 목록 — 고른 반장이 결과만 직접등록(SIF-3)인지 판정에 쓴다 */
  partners: Partner[]
}

export function useGanttPanel({
  tasks, onTaskPanelChange, tasksRef, depsRef, selectedSite, user, setTasks, setRefreshKey,
  invalidateSignals, selectedTaskIds, setSelectedTaskIds, scheduleOnlyCodes, partners,
}: UseGanttPanelArgs) {
  // 결과만 직접등록(SIF-3) 반장 — 고르는 즉시 그 반장으로 확정(사유·발송 없이 PATCH)
  const isDirectPartner = (partnerId: number | null) =>
    partnerId != null && isDirectRecord(partners.find(p => p.partnerId === partnerId)?.commYn)
  const [selectedPanelTaskId, setSelectedPanelTaskId] = useState<number | null>(null)
  const [panelForeman,        setPanelForeman]        = useState('')
  const [panelPartnerId,      setPanelPartnerId]      = useState<number | null>(null)
  // 확정 반장 변경 사유 코드(미선택=''). PartnerCard 의 사유 select 와 [저장]이 함께 쓴다.
  const [panelChangeReason,   setPanelChangeReason]   = useState('')
  // 발송 결과 문구(기획서 #gdOk, 3초 표시)
  const [panelNotice,         setPanelNotice]         = useState('')
  const [panelWorkTypeName,   setPanelWorkTypeName]   = useState('')
  // 간트 좌측 행 이름(공종 표시명) 인라인 편집 — taskId(row.bars[0]) 기준
  const [localRowNames,       setLocalRowNames]       = useState<Record<number, string>>({})
  const [panelPredIds,        setPanelPredIds]        = useState<number[]>([])
  const [panelMemo,           setPanelMemo]           = useState('')
  const [panelSaving,         setPanelSaving]         = useState(false)
  const [panelError,          setPanelError]          = useState('')

  // ── 상세창 편집 dirty 추적·이탈 확인(GP-48) ─────────────────────
  // 공종명·메모·선행공종·시공파트너 지정·해제는 바꾸면 바로 저장되므로(commitPanel) 미저장으로 보지 않는다.
  // 남는 미저장은 확정 반장 변경(사유 필수)뿐 — 자동 저장하지 않고 시공파트너 카드의 [변경하고 보내기]로만 반영된다.
  const panelDirty = (() => {
    if (selectedPanelTaskId === null) return false
    const t = tasks.find(x => x.taskId === selectedPanelTaskId)
    if (!t) return false
    return getPartnerSaveKind(t, panelPartnerId, isDirectPartner(panelPartnerId)) === 'change' || panelChangeReason !== ''
  })()
  const panelDirtyRef = useRef(false)
  panelDirtyRef.current = panelDirty
  const panelTaskIdRef = useRef<number | null>(null)
  panelTaskIdRef.current = selectedPanelTaskId
  // 이탈 확인 창이 기다리는 동작(상세창 닫기·다른 공종 선택)
  const [leavePrompt, setLeavePrompt] = useState<{ run: () => void } | null>(null)
  const guardPanelLeave = useCallback((run: () => void) => {
    if (panelDirtyRef.current) setLeavePrompt({ run })
    else run()
  }, [])

  const closePanelGuarded = useCallback(() => guardPanelLeave(() => setSelectedPanelTaskId(null)), [guardPanelLeave])
  // 스케줄 전용 행정 공정(세대 동의서 청구·공사신고서 제출·엘리베이터 사용 신청 등)은 시공·자재가 없어 상세창이 없다 —
  // 눌러도 열지 않고, 다른 공종 상세창이 열려 있으면 닫는다(수정 중이면 같은 이탈 확인을 거침).
  const scheduleOnlyRef = useRef(scheduleOnlyCodes)
  scheduleOnlyRef.current = scheduleOnlyCodes
  const hasNoDetail = useCallback((taskId: number) => {
    const code = tasksRef.current.find(t => t.taskId === taskId)?.workTypeCode
    return !!code && scheduleOnlyRef.current.has(code)
  }, [tasksRef])
  const selectPanelTaskGuarded = useCallback((taskId: number) => {
    if (panelTaskIdRef.current === taskId) return   // 이미 열린 공종 재클릭은 이탈이 아님
    guardPanelLeave(() => setSelectedPanelTaskId(hasNoDetail(taskId) ? null : taskId))
  }, [guardPanelLeave, hasNoDetail])
  // 막대 클릭(B-15) — 이미 열린 공종의 막대를 다시 누르면 상세창을 닫는다(수정 중이면 같은 이탈 확인을 거침), 다른 막대는 전환
  const togglePanelTaskGuarded = useCallback((taskId: number) => {
    if (panelTaskIdRef.current === taskId || hasNoDetail(taskId)) { guardPanelLeave(() => setSelectedPanelTaskId(null)); return }
    guardPanelLeave(() => setSelectedPanelTaskId(taskId))
  }, [guardPanelLeave, hasNoDetail])

  // 공종 편집 패널 열림/닫힘을 부모에게 알림 (고객·현장 패널 숨기기용)
  useEffect(() => {
    onTaskPanelChange?.(selectedPanelTaskId !== null)
  }, [selectedPanelTaskId, onTaskPanelChange])

  // 열린 공종의 시공파트너 서버 값 — 마지막으로 상세창 선택값에 맞춘 값(아래 동기화 effect 가 비교에 쓴다)
  const serverPartnerRef = useRef<number | null>(null)

  // 선택된 공종이 바뀌면 패널 폼 초기화
  useEffect(() => {
    if (selectedPanelTaskId === null) return
    const task = tasksRef.current.find(t => t.taskId === selectedPanelTaskId)
    setPanelForeman(task?.foremanName ?? '')
    setPanelPartnerId(task?.assigneePartnerId ?? null)
    setPanelChangeReason('')
    setPanelNotice('')
    setPanelMemo(task?.description ?? '')
    setPanelWorkTypeName(task?.workTypeName ?? '')
    const predIds = depsRef.current
      .filter(d => d.successorTaskId === selectedPanelTaskId && d.dependencyType === 'FS')
      .map(d => d.predecessorTaskId)
    setPanelPredIds(predIds)
    setPanelError('')
    serverPartnerRef.current = task?.assigneePartnerId ?? null
  }, [selectedPanelTaskId])

  // 열린 공종의 시공파트너가 서버에서 바뀌면(방식 변경에 따른 자동배정·해제 등) 상세창 선택값도 서버 값으로 맞춘다.
  // 마지막으로 맞춘 서버 값(serverPartnerRef)과 달라졌을 때만 덮어써, 사용자가 고르기만 하고 저장 전인 선택은 지키지 않는다
  // — 서버 쪽 변경이 우선(그대로 두면 [저장]이 낡은 선택으로 반장 지정·해제를 다시 보내 거절되거나 엉뚱하게 해제된다).
  useEffect(() => {
    if (selectedPanelTaskId === null) return
    const task = tasks.find(t => t.taskId === selectedPanelTaskId)
    if (!task) return
    const serverId = task.assigneePartnerId ?? null
    if (serverId === serverPartnerRef.current) return
    serverPartnerRef.current = serverId
    setPanelPartnerId(serverId)
    setPanelForeman(task.foremanName ?? '')
    setPanelChangeReason('')
  }, [tasks, selectedPanelTaskId])

  // ── 사이드 패널 저장 / 삭제 ──────────────────────────────────────
  // 착공 D-2 이내 수동 배정 즉시 확정 확인(서버 409 IMMEDIATE_CONFIRM_REQUIRED)
  const immediate = useImmediateConfirm()

  // ── 상세창 실시간 저장(2026-10-29, [저장] 버튼 없음) ──────────────────
  // 공종명·메모는 입력칸을 벗어날 때, 선행공종은 고를 때마다, 시공파트너 지정·해제는 고르는 즉시 저장한다(PATCH /schedule/tasks/{id}).
  // 확정 반장 변경(사유 필수)은 자동 저장하지 않는다 — 변경 즉시 이전 반장 쪽 처리가 일어나므로 시공파트너 카드의 [변경하고 보내기]로만 한다.
  // 저장은 한 줄로 세워(saveChainRef) 빠르게 연달아 바꿔도 앞 저장이 끝난 뒤 다음이 나간다. 값은 부른 시점의 값을 쓴다.
  type PanelValues = { memo: string; name: string; predIds: number[]; partnerId: number | null }
  const saveChainRef = useRef<Promise<void>>(Promise.resolve())

  const savePanel = async (taskId: number, v: PanelValues, withPartner: boolean) => {
    if (!selectedSite?.scheduleId) return
    const curTask = tasksRef.current.find(t => t.taskId === taskId)
    if (!curTask) return
    const basePred = depsRef.current
      .filter(d => d.successorTaskId === taskId && d.dependencyType === 'FS')
      .map(d => d.predecessorTaskId)
    const kind = withPartner ? getPartnerSaveKind(curTask, v.partnerId, isDirectPartner(v.partnerId)) : 'none'
    const revertPartner = () => {
      if (panelTaskIdRef.current !== taskId) return
      setPanelPartnerId(curTask.assigneePartnerId ?? null)
      setPanelForeman(curTask.foremanName ?? '')
    }
    if (kind === 'release-blocked') { setPanelError(FOREMAN_CHANGE_MESSAGE.releaseDenied); revertPartner(); return }
    const partnerKind = kind === 'patch' || kind === 'release' ? kind : 'none'
    const fieldsChanged = v.memo !== (curTask.description ?? '') || v.name !== curTask.workTypeName || !samePredecessorSet(basePred, v.predIds)
    if (!fieldsChanged && partnerKind === 'none') return
    const hdrs = {
      ...(user?.compId   ? { 'X-Comp-Id': String(user.compId)   } : {}),
      ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
    }
    setPanelSaving(true); setPanelError(''); setPanelNotice('')
    try {
      const patchRun = await immediate.run(ok => apiClient.patch(`/schedule/tasks/${taskId}`, {
        ...(partnerKind === 'patch' && { assigneePartnerId: v.partnerId }),
        ...(partnerKind === 'patch' && ok && { confirmImmediate: true }),
        // 선행공종은 선택한 전체 목록을 보낸다(서버가 기존 선행을 지우고 이 목록으로 바꿈, 비우면 선행 해제).
        ...buildPredecessorPatch(v.predIds),
        // 비운 메모는 빈 문자열로 보낸다(서버는 null=변경 없음, ""=지움으로 처리).
        scheduleMemo:      v.memo ?? '',
        // 빈 값으로 저장하면 BE가 TM_WORK_TYPE 표준 공종명으로 되돌린다(TaskService#patchTask).
        workTypeDetailName: v.name.trim(),
      }, { headers: hdrs }))
      // 착공 D-2 이내 즉시 확정 확인에서 취소 — 시공파트너 선택만 서버 값으로 되돌린다(다른 칸은 다음 저장 때 함께 나간다)
      if (patchRun.cancelled) { revertPartner(); return }
      // 착공 임박 즉시 확정만 안내(반장에게 알림이 감). 결과만 직접등록 확정은 알리지 않으므로 이 문구를 띄우지 않는다
      if (partnerKind === 'patch' && isFixedPatchResponse(patchRun.value) && !isDirectPartner(v.partnerId)) toast.success(IMMEDIATE_CONFIRM_SUCCESS_MESSAGE)
      if (partnerKind === 'release' && curTask.assigneePartnerId != null) {
        await releasePartner(taskId, curTask.assigneePartnerId)
      }
      setTasks(prev => prev.map(t =>
        t.taskId === taskId ? { ...t, description: v.memo, workTypeName: v.name.trim() || t.workTypeName } : t
      ))
      if (panelTaskIdRef.current === taskId) setPanelNotice('저장됐어요')
      setRefreshKey(k => k + 1)
      void invalidateSignals()
    } catch (err) {
      setPanelError(isInvalidPredecessorError(err) ? INVALID_PREDECESSOR_MESSAGE : '저장 중 오류가 발생했습니다.')
      // 선행공종 순환 등으로 거절되면 선택을 서버 값으로 되돌려 화면과 저장 값이 어긋나지 않게 한다
      if (panelTaskIdRef.current === taskId) {
        if (!samePredecessorSet(basePred, v.predIds)) setPanelPredIds(basePred)
        if (partnerKind !== 'none') revertPartner()
      }
    } finally {
      setPanelSaving(false)
    }
  }

  /** 지금 상세창 값으로 저장을 줄 세운다. over 로 방금 바꾼 값을 넘기면(상태 반영 전) 그 값을 쓴다. partner=true 면 시공파트너 지정·해제도 함께 */
  const commitPanel = (over: Partial<PanelValues> = {}, opts: { partner?: boolean } = {}) => {
    const taskId = selectedPanelTaskId
    if (taskId === null) return
    const v: PanelValues = {
      memo: over.memo ?? panelMemo,
      name: over.name ?? panelWorkTypeName,
      predIds: over.predIds ?? panelPredIds,
      partnerId: 'partnerId' in over ? (over.partnerId ?? null) : panelPartnerId,
    }
    saveChainRef.current = saveChainRef.current.then(() => savePanel(taskId, v, !!opts.partner)).catch(() => undefined)
  }

  // 간트 좌측 행 이름(공종 표시명) 인라인 저장 — 비우면 BE가 TM_WORK_TYPE 표준 공종명으로 되돌린다.
  const handleRowNameBlur = async (taskId: number) => {
    const name = localRowNames[taskId]
    if (name === undefined) return
    const hdrs = {
      ...(user?.compId   ? { 'X-Comp-Id': String(user.compId)   } : {}),
      ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
    }
    try {
      await apiClient.patch(`/schedule/tasks/${taskId}`, {
        workTypeDetailName: name.trim(), updatePredecessor: false,
      }, { headers: hdrs })
      setLocalRowNames(prev => { const n = { ...prev }; delete n[taskId]; return n })
      setRefreshKey(k => k + 1)
    } catch {
      toast.error('공종명 저장 중 오류가 발생했습니다.')
    }
  }

  const handleDeleteTask = async () => {
    if (!selectedSite?.scheduleId || selectedPanelTaskId === null) return
    const delTask = tasks.find(t => t.taskId === selectedPanelTaskId)
    if (!(await confirmTaskDelete({
      scheduleId: selectedSite.scheduleId,
      tasks: delTask ? [delTask] : [],
      base: buildTaskDeleteConfirm({ requestCount: delTask?.requestCount }),
    }))) return
    const hdrs = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
    try {
      await apiClient.delete(`/schedule/tasks/${selectedPanelTaskId}`, { headers: hdrs })
      setSelectedPanelTaskId(null)
      setRefreshKey(k => k + 1)
      void invalidateSignals()
    } catch {
      toast.error('삭제 중 오류가 발생했습니다.')
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedTaskIds.size === 0) return
    const selectedTasks = tasks.filter(t => selectedTaskIds.has(t.taskId))
    const selectedCounts = sumCounts(selectedTasks)
    if (!(await confirmTaskDelete({
      scheduleId: selectedSite?.scheduleId,
      tasks: selectedTasks,
      base: buildTaskDeleteConfirm({ multiCount: selectedTaskIds.size, requestCount: selectedCounts.requestCount }),
    }))) return
    const hdrs = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
    // 일부만 성공해도 성공한 공종은 목록·선택에서 빼고 실패한 공종만 남긴다(FR-C-126, FR-C-127).
    const ids = [...selectedTaskIds]
    const results = await Promise.allSettled(ids.map(id => apiClient.delete(`/schedule/tasks/${id}`, { headers: hdrs })))
    const deleted = ids.filter((_, i) => results[i].status === 'fulfilled')
    const failed = ids.length - deleted.length
    if (deleted.length > 0) {
      setTasks(prev => prev.filter(t => !deleted.includes(t.taskId)))
      setSelectedTaskIds(prev => new Set([...prev].filter(id => !deleted.includes(id))))
      setRefreshKey(k => k + 1)
      void invalidateSignals()
    }
    if (failed > 0) {
      toast.error(`${failed}개 공종을 삭제하지 못했습니다.${deleted.length > 0 ? ` ${deleted.length}개는 삭제되었습니다.` : ''}`)
    }
  }

  return {
    panelDirty, setSelectedPanelTaskId, selectPanelTaskGuarded, togglePanelTaskGuarded, closePanelGuarded, setPanelForeman,
    setPanelPartnerId, setPanelNotice, panelNotice, selectedPanelTaskId, handleDeleteSelected, localRowNames,
    setLocalRowNames, handleRowNameBlur, panelWorkTypeName, setPanelWorkTypeName, panelPartnerId,
    panelForeman, panelChangeReason, setPanelChangeReason, panelPredIds, setPanelPredIds, panelMemo,
    setPanelMemo, panelError, panelSaving, commitPanel, handleDeleteTask, leavePrompt, setLeavePrompt,
    immediate,
  }
}
